import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Bank, CheckCircle, Warning, DownloadSimple, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DepositPortal() {
  const { token } = useParams();
  const t = useT();
  const [deposit, setDeposit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [showDispute, setShowDispute] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/direct-deposits/public/${token}`);
      setDeposit(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || t("portal.unavailable.body"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/direct-deposits/public/${token}/confirm`);
      toast.success(t("portal.toast.confirmed"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("portal.toast.confirm_failed"));
    } finally {
      setBusy(false);
    }
  };

  const submitDispute = async () => {
    if (reason.trim().length < 4) {
      toast.error(t("portal.dispute.too_short"));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/direct-deposits/public/${token}/dispute`, { reason: reason.trim() });
      toast.success(t("portal.toast.reported"));
      setShowDispute(false);
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("portal.toast.report_failed"));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/direct-deposits/public/${token}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `zcardon-deposit-${deposit?.reference_number || token.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("portal.toast.pdf_failed"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center" data-testid="portal-loading">
        <p className="text-[#4A5C50]">{t("portal.loading")}</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-6" data-testid="portal-error">
        <div className="max-w-md w-full bg-white border border-[#E5E0D8] rounded-2xl p-8 text-center">
          <Warning size={36} weight="duotone" className="text-[#D96C4A] mx-auto" />
          <h1 className="serif text-2xl text-[#112318] mt-3">{t("portal.unavailable.title")}</h1>
          <p className="text-sm text-[#4A5C50] mt-2">{error}</p>
          <p className="text-xs text-[#4A5C50] mt-4">
            {t("portal.unavailable.hint")}
          </p>
        </div>
      </div>
    );
  }

  const d = deposit;
  const confirmed = !!d.receiver_confirmed_at;
  const disputed = !!d.receiver_disputed_at;
  const isApproved = d.status === "approved";

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center gap-2">
          <Bank size={22} weight="duotone" className="text-[#184A30]" />
          <span className="serif text-xl text-[#112318]">Zcardon</span>
          <span className="ml-auto text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">{t("portal.brand")}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 space-y-5" data-testid="portal-deposit">
        <div className="text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">{t("portal.eyebrow", { sender: d.sender_name })}</p>
          <p className="serif text-5xl text-[#184A30] mt-2" data-testid="portal-amount">
            ${Number(d.amount).toFixed(2)} <span className="text-2xl text-[#4A5C50]">{d.currency}</span>
          </p>
          <p className="text-xs mono text-[#4A5C50] mt-3">
            {t("portal.reference")} <b>{d.reference_number}</b> · {new Date(d.created_at).toLocaleDateString()}
          </p>
        </div>

        {!isApproved && (
          <div className="rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-5 text-[#8C6B21]" data-testid="portal-pending">
            <p className="text-sm font-semibold">{t("portal.pending.title")}</p>
            <p className="text-xs mt-1">
              {t("portal.pending.body")}
            </p>
          </div>
        )}

        {isApproved && (
          <>
            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 space-y-2 text-sm" data-testid="portal-details">
              <Row label={t("portal.row.bank")} value={d.bank_name} />
              <Row label={t("portal.row.account")} value={d.account_number_masked} mono />
              <Row label={t("portal.row.routing")} value={d.routing_number} mono />
              {d.memo && <Row label={t("portal.row.memo")} value={d.memo} />}
            </div>

            {confirmed && (
              <div className="rounded-2xl border border-[#184A30]/30 bg-[#184A30]/5 p-5 text-[#184A30]" data-testid="portal-confirmed">
                <div className="flex items-start gap-3">
                  <CheckCircle size={22} weight="fill" />
                  <div>
                    <p className="font-semibold">{t("portal.confirmed.title")}</p>
                    <p className="text-xs mt-1 opacity-80">
                      {t("portal.confirmed.body", { when: new Date(d.receiver_confirmed_at).toLocaleString() })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {disputed && (
              <div className="rounded-2xl border border-[#D96C4A]/40 bg-[#D96C4A]/10 p-5 text-[#B04E2F]" data-testid="portal-disputed">
                <div className="flex items-start gap-3">
                  <Warning size={22} weight="fill" />
                  <div>
                    <p className="font-semibold">{t("portal.disputed.title")}</p>
                    <p className="text-xs mt-1 opacity-80">
                      {t("portal.disputed.body", { when: new Date(d.receiver_disputed_at).toLocaleString() })}
                    </p>
                    {d.receiver_dispute_reason && (
                      <p className="text-xs mt-2 italic">"{d.receiver_dispute_reason}"</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!confirmed && !disputed && (
              <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 space-y-3" data-testid="portal-actions">
                <p className="text-sm text-[#112318]">
                  {t("portal.actions.body")}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={confirm}
                    disabled={busy}
                    className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white h-11"
                    data-testid="portal-confirm-btn"
                  >
                    <CheckCircle size={16} className="mr-2" /> {t("portal.actions.confirm")}
                  </Button>
                  <Button
                    onClick={() => setShowDispute((s) => !s)}
                    variant="outline"
                    className="rounded-xl border-[#D96C4A]/40 text-[#B04E2F] hover:bg-[#D96C4A]/10 h-11"
                    data-testid="portal-dispute-toggle"
                  >
                    <Warning size={16} className="mr-2" /> {t("portal.actions.flag")}
                  </Button>
                </div>
                {showDispute && (
                  <div className="space-y-2 pt-2" data-testid="portal-dispute-form">
                    <Textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t("portal.dispute.placeholder")}
                      rows={3}
                      data-testid="portal-dispute-reason"
                    />
                    <Button
                      onClick={submitDispute}
                      disabled={busy}
                      className="rounded-xl bg-[#B04E2F] hover:bg-[#8a3a20] text-white h-11"
                      data-testid="portal-dispute-submit"
                    >
                      {t("portal.dispute.submit")}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5">
              <Button
                variant="outline"
                onClick={downloadPdf}
                className="w-full rounded-xl h-11"
                data-testid="portal-download-pdf"
              >
                <DownloadSimple size={16} className="mr-2" /> {t("portal.download_pdf")}
              </Button>
              <p className="text-[11px] text-[#4A5C50] mt-3 leading-relaxed">
                <ShieldCheck size={13} weight="duotone" className="inline mr-1" />
                {t("portal.privacy")} <a href="mailto:support@zcardon.com" className="underline">support@zcardon.com</a>.
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] w-20 shrink-0">{label}</span>
      <span className={`text-[#112318] ${mono ? "mono" : ""} flex-1 truncate`}>{value}</span>
    </div>
  );
}
